import orderBy from 'lodash/orderBy'
import { action, computed, makeObservable, observable } from 'mobx'
import { dependsOn, trackedAction } from 'mobx-async'

import arriereBackoffice, {
  GuestsSorting,
  SortOrder,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { WalletTransactionKind } from '@avantstay/arriere-clients/dist/arrierePublic'
import { Alert, Awaited } from '@avantstay/backoffice-core'

import { handleStoreError } from '@new/__global/utils/handleStoreError'
import { BookingStore } from '@new/domains/bookings/stores/BookingStore'
import {
  AddBookingGuestVariables,
  BookingResults,
  CreateBookingGuestVariables,
  GuestBooking,
  GuestCredit,
  GuestCreditEdit,
  GuestCreditWithdraw,
  GuestInfoTabs,
  GuestProjectionCreditHistoryFragment,
  RemoveGuestVariables,
  UpdateGuestAdditionalEmailsVariables,
  UpdateGuestNameVariables,
  UpdateGuestVariables,
} from '@new/domains/people/guests/Guests.types'

import { SidePanelStore } from '@/_new/__global/stores/SidePanelStore'
import { Guest } from '@/types/servicesTypes/Guests.types'
import { arriere } from '@arriere/index'
import { ROUTES } from '@routes'
import { computedRouteParam } from '@stores/computedRouteParam'
import { trackError, trackEvent } from '@tracking/customEventReport'

import { getDamageStatus, getEmailsToUpdate, getRefunds } from '../Guests.utils'

const defaultPageSize = 30

type GuestsLists = Awaited<ReturnType<typeof arriere.guests.getGuestsList>>

class _GuestsStore {
  guestId = ''

  guestEmail = ''

  guestsListsCache?: GuestsLists = undefined

  guestInfoTab = GuestInfoTabs.GUEST_INFO
  // -------- Computed Values

  filterTerm = computedRouteParam<string | undefined>('filter', {
    patterns: [ROUTES.guests.path],
    format: JSON.stringify,
    parse: JSON.parse,
    defaultValue: undefined,
  })

  guestTags = computedRouteParam<string[] | undefined>('guestTags', {
    patterns: [ROUTES.guests.path],
    format: (values?: string[] | null) => (values ? values.join(',') : ''),
    parse: valueString => (valueString ? valueString.split(',') : undefined),
    defaultValue: undefined,
  })

  sortBy = computedRouteParam<GuestsSorting | undefined>('sortBy', {
    patterns: [ROUTES.guests.path],
    parse: v => v?.toUpperCase() as GuestsSorting,
    defaultValue: undefined,
  })

  sortOrder = computedRouteParam<SortOrder | undefined>('sortOrder', {
    patterns: [ROUTES.guests.path],
    parse: v => v?.toUpperCase() as SortOrder,
    defaultValue: undefined,
  })

  page = computedRouteParam<number>('page', {
    patterns: [ROUTES.guests.path],
    parse: value => parseFloat(value),
    defaultValue: 1,
  })

  pageSize = computedRouteParam<number>('pageSize', {
    patterns: [ROUTES.guests.path],
    parse: value => parseFloat(value),
    defaultValue: defaultPageSize,
  })

  constructor() {
    makeObservable(this, {
      guestId: observable,
      guestEmail: observable,
      guestsListsCache: observable,
      guestInfoTab: observable,
      guestsList: computed,
      guestInfo: computed,
      guestsCountByTag: computed,
      guestBookings: computed,
      guestCreditsHistory: computed,
      guestCreditsHistoryActive: computed,
      guestCreditsHistoryInactive: computed,
      isClearFilterDisabled: computed,
      setGuestTags: action.bound,
      setGuestsListsCache: action.bound,
      setSortBy: action.bound,
      setSortOrder: action.bound,
      setPagination: action.bound,
      setPaginationSize: action.bound,
      resetPagination: action.bound,
      setFilterTerm: action.bound,
      clearAllFilters: action.bound,
      setGuestId: action.bound,
      setGuestEmail: action.bound,
      setGuestInfoTab: action.bound,
    })

    this.createBookingGuest = trackedAction<any>(this.createBookingGuest.bind(this))
    this.addBookingGuest = trackedAction<any>(this.addBookingGuest.bind(this))
    this.findGuests = trackedAction<any>(this.findGuests.bind(this))
    this.withdrawCredit = trackedAction<any>(this.withdrawCredit.bind(this))
    this.changeCreditExpirationDate = trackedAction<any>(this.changeCreditExpirationDate.bind(this))
    this.createCreditToGuest = trackedAction<any>(this.createCreditToGuest.bind(this))
    this.removeGuest = trackedAction<any>(this.removeGuest.bind(this))
    this.updateGuestAdditionalEmails = trackedAction<any>(
      this.updateGuestAdditionalEmails.bind(this),
    )
    this.updateGuestInfo = trackedAction<any>(this.updateGuestInfo.bind(this))
    this.updateGuestName = trackedAction<any>(this.updateGuestName.bind(this))
    this.reloadGuestInfo = trackedAction<any>(this.reloadGuestInfo.bind(this))
    this.reloadGuestsList = trackedAction<any>(this.reloadGuestsList.bind(this))
  }

  get guestsList() {
    const { createCreditToGuest, updateGuestInfo, withdrawCredit, reloadGuestsList } = this
    dependsOn(createCreditToGuest, updateGuestInfo, withdrawCredit, reloadGuestsList)

    const pagination = { page: this.page.get(), size: this.pageSize.get() }
    const guestTags = this.guestTags.get()?.map(Number)
    const sorting = (this.sortBy.get() as GuestsSorting) ?? GuestsSorting.accountCreatedAt
    const sortOrder = this.sortOrder.get() ?? SortOrder.ascending
    const terms = this.filterTerm.get()

    return arriere.guests.getGuestsList({
      terms,
      pagination,
      guestTags,
      sorting,
      sortOrder,
    })
  }

  get guestInfo(): Promise<Guest | undefined> {
    const {
      createCreditToGuest,
      setGuestId,
      updateGuestInfo,
      updateGuestName,
      updateGuestAdditionalEmails,
      guestId,
      setGuestEmail,
      withdrawCredit,
      reloadGuestInfo,
    } = this

    if (!guestId) return Promise.resolve(undefined)

    dependsOn(
      createCreditToGuest,
      setGuestId,
      updateGuestInfo,
      updateGuestName,
      updateGuestAdditionalEmails,
      withdrawCredit,
      reloadGuestInfo,
    )

    return arriere.guests.getGuestById(guestId).then(results => {
      setGuestEmail(results.emails[0])
      return results
    })
  }

  get guestsCountByTag() {
    dependsOn(this.updateGuestInfo)
    return arriere.guests.getGuestsCountByTag()
  }

  get guestBookings(): Promise<GuestBooking[]> {
    const { guestInfo } = this

    return guestInfo.then(async info => {
      if (info) {
        const { guestId } = info

        const response = await arriere.guests.bookingsOfGuest(guestId)

        if (response) {
          const allBookings = Object.values(response).reduce<GuestBooking[]>(
            (previous, current: BookingResults) => [...previous, ...(current.results || current)],
            [],
          )

          return orderBy(allBookings, booking => booking.period.start, 'desc').map(booking => {
            const refunds = getRefunds(booking)
            const { hasDamage, damageHasBeenPaid } = getDamageStatus(booking)

            return {
              ...booking,
              hasDamage,
              damageHasBeenPaid,
              refunds,
            }
          })
        }
      }

      return []
    })
  }

  get guestCreditsHistory(): Promise<GuestProjectionCreditHistoryFragment[]> {
    const { setGuestEmail, changeCreditExpirationDate, withdrawCredit, guestId } = this
    dependsOn(setGuestEmail, changeCreditExpirationDate, withdrawCredit)

    if (guestId) {
      return arriere.guests.getGuestCreditsHistory(guestId)
    }

    return Promise.resolve([])
  }

  get guestCreditsHistoryActive(): Promise<GuestProjectionCreditHistoryFragment[]> {
    const { guestCreditsHistory } = this
    dependsOn(guestCreditsHistory)

    return guestCreditsHistory.then(credits => {
      return credits?.filter(
        ({ transaction }) => transaction.kind === WalletTransactionKind.deposit,
      )
    })
  }

  get guestCreditsHistoryInactive(): Promise<GuestProjectionCreditHistoryFragment[]> {
    const { guestCreditsHistory } = this
    dependsOn(guestCreditsHistory)

    return guestCreditsHistory.then(credits =>
      credits?.filter(({ transaction }) => transaction.kind === WalletTransactionKind.withdrawal),
    )
  }

  get isClearFilterDisabled(): boolean {
    const { filterTerm, sortBy, sortOrder, guestTags } = this

    return [
      filterTerm.get() === undefined,
      sortBy.get() === undefined,
      sortOrder.get() === undefined,
      guestTags.get() === undefined,
    ].every(Boolean)
  }

  setGuestTags(value?: string[]): void {
    this.resetPagination()
    this.guestTags.replace(value)
  }

  setGuestsListsCache(guestsListsCache?: GuestsLists): void {
    this.guestsListsCache = guestsListsCache
  }

  setSortBy(sortBy?: GuestsSorting): void {
    this.resetPagination()
    this.sortBy.replace(sortBy)
  }

  setSortOrder(sortOrder?: SortOrder): void {
    this.resetPagination()
    this.sortOrder.replace(sortOrder)
  }

  setPagination(page: number): void {
    this.page.replace(page + 1)
  }

  setPaginationSize(pageSize: number): void {
    this.page.replace(1)
    this.pageSize.replace(pageSize)
  }

  resetPagination(): void {
    this.page.push(1)
    this.pageSize.replace(defaultPageSize)
  }

  setFilterTerm(term?: string): void {
    this.resetPagination()
    this.filterTerm.push(term)
  }

  clearAllFilters(): void {
    this.setFilterTerm(undefined)
    this.setGuestTags(undefined)
    this.setSortBy(undefined)
    this.setSortOrder(undefined)
  }

  setGuestId(guestId: string): void {
    this.guestId = guestId
  }

  setGuestEmail(guestEmail: string): void {
    this.guestEmail = guestEmail
  }

  setGuestInfoTab(tab: GuestInfoTabs): void {
    this.guestInfoTab = tab
  }

  async updateGuestInfo({ id, tagsIds, note }: UpdateGuestVariables) {
    try {
      await arriere.guests.updateGuestInfo({ id, tagsIds, note }).then(
        response => {
          if (response) {
            Alert.Success('Updated guest successfully')
            SidePanelStore.setCurrentSidePanel(undefined)
            BookingStore.updateBookingValuesFromServer()
          }
        },
        error => {
          Alert.Error(`We couldn't update guest: ${error}`)
        },
      )

      this.setGuestInfoTab(GuestInfoTabs.GUEST_INFO)
    } catch (e) {
      Alert.Error('Oops! We were not able to process your request.')
    }
  }

  async updateGuestName({ id, fullName }: UpdateGuestNameVariables) {
    try {
      await arriere.guests.updateGuestName({ id, fullName })
      this.reloadGuestsList()
      BookingStore.setRenamedMainGuest({ guestId: id, fullName })
      BookingStore.updateBookingValuesFromServer()
    } catch (error) {
      handleStoreError({
        action: 'Failed to update a guest name',
        error,
        context: { guestId: id },
        userMessage: "We couldn't update the guest name, try again.",
      })
    }
  }

  async updateGuestAdditionalEmails({
    id,
    additionalEmails,
    originalEmails,
  }: UpdateGuestAdditionalEmailsVariables) {
    try {
      const emailsToUpdate = getEmailsToUpdate({ additionalEmails, originalEmails })

      await Promise.all([
        emailsToUpdate.emailsToAdd.map(email => arriere.guests.addAdditionalEmails({ id, email })),
        emailsToUpdate.emailsToRemove.map(email =>
          arriere.guests.removeAdditionalEmails({ id, email }),
        ),
      ]).then(
        () => {
          Alert.Success('Updated guest additional emails successfully')
          SidePanelStore.setCurrentSidePanel(undefined)
          BookingStore.updateBookingValuesFromServer()
        },
        error => {
          Alert.Error(`We couldn't update guest: ${error}`)
        },
      )

      this.setGuestInfoTab(GuestInfoTabs.GUEST_INFO)
    } catch (e) {
      Alert.Error('Oops! We were not able to process your request.')
    }
  }

  async removeGuest({ bookingId, personId }: RemoveGuestVariables) {
    try {
      await arriereBackoffice.mutations
        .guests({ removeGuest: { __args: { bookingId, personId } } })
        .then(
          response => {
            if (response) {
              Alert.Success('Removed guest successfully')
              BookingStore.updateBookingValuesFromServer()
            }
          },
          error => {
            Alert.Error(`We couldn't remove guest: ${error}`)
          },
        )
    } catch (e) {
      Alert.Error('Oops! We were not able to process your request.')
    }
  }

  async createCreditToGuest({
    personId,
    cents,
    expiration,
    reason,
    relatedBookingId,
  }: GuestCredit): Promise<void> {
    trackEvent('Guest: Attempting to add credits to guest.', {
      personId,
      cents,
      expiration,
      reason,
      relatedBookingId,
    })

    try {
      await arriere.guests
        .addCreditToGuest({ personId, cents, expiration, reason, relatedBookingId })
        .then(
          response => {
            if (response) {
              trackEvent('Guest: Credits added to guest successfully!', {
                personId,
                cents,
                expiration,
                reason,
                relatedBookingId,
              })
              Alert.Success('Credits added to guest successfully!')
            } else {
              throw Error
            }
          },
          error => {
            Alert.Error(`We couldn't add credit to guest: ${error}`)

            trackError('Guest: Error adding credits to guest.', {
              personId,
              cents,
              expiration,
              reason,
              relatedBookingId,
              error,
            })
          },
        )
      this.setGuestInfoTab(GuestInfoTabs.TRAVEL_CREDITS)
    } catch (error) {
      Alert.Error('Oops! We were not able to process your request.')

      trackError('Guest: Error adding credits to guest.', {
        personId,
        cents,
        expiration,
        reason,
        relatedBookingId,
        error,
      })
    }
  }

  async changeCreditExpirationDate({
    personId,
    walletTransactionId,
    expiration,
  }: GuestCreditEdit): Promise<void> {
    trackEvent('Guest: Attempting to edit credits in guest wallet.', {
      personId,
      walletTransactionId,
      expiration,
    })

    try {
      await arriere.guests
        .changeCreditExpirationDate({ personId, walletTransactionId, expiration })
        .then(
          response => {
            if (response) {
              trackEvent('Guest: Credits expiration date was changed successfully!', {
                personId,
                walletTransactionId,
                expiration,
              })
              Alert.Success('Credits expiration date was changed successfully!')
            } else {
              throw Error
            }
          },
          error => {
            Alert.Error(`We couldn't edit credit expiration date: ${error}`)
            trackError('Guest: Error editing credits expiration date.', {
              personId,
              walletTransactionId,
              expiration,
              error,
            })
          },
        )

      this.setGuestInfoTab(GuestInfoTabs.TRAVEL_CREDITS)
    } catch (error) {
      Alert.Error('Oops! We were not able to process your request.')

      trackError('Guest: Error editing credits expiration date.', {
        personId,
        walletTransactionId,
        expiration,
        error,
      })
    }
  }

  async withdrawCredit({
    personId,
    walletTransactionId,
    amount,
    reason,
  }: GuestCreditWithdraw): Promise<void> {
    trackEvent('Guest: Attempting to withdraw credits from guest wallet.', {
      personId,
      walletTransactionId,
      amount,
      reason,
    })

    try {
      await arriere.guests.withdrawCredit({ personId, walletTransactionId, amount, reason }).then(
        response => {
          if (response) {
            trackEvent('Guest: Credits were withdrawn successfully!', {
              personId,
              walletTransactionId,
              amount,
              reason,
            })
            Alert.Success('Credits were withdrawn successfully!')
            this.setGuestInfoTab(GuestInfoTabs.TRAVEL_CREDITS)
          } else {
            throw Error
          }
        },
        error => {
          Alert.Error(`We couldn't withdraw credit from wallet: ${error}`)

          trackError('Guest: Error withdrawn credits from wallet.', {
            personId,
            walletTransactionId,
            amount,
            reason,
            error,
          })
        },
      )
    } catch (error) {
      Alert.Error('Oops! We were not able to process your request.')

      trackError('Guest: Error withdrawn credits from wallet.', {
        personId,
        walletTransactionId,
        amount,
        reason,
        error,
      })
    }
  }

  async findGuests(term: string) {
    return arriere.guests.findGuests(term)
  }

  reloadGuestInfo() {}

  reloadGuestsList() {
    // getGuestsList reads through `memoRaw` (moize, 30s maxAge); drop its cache so the
    // guestsList recompute triggered by this action actually refetches
    const memoizedGuestsQuery = arriereBackoffice.queries.guests.memoRaw
    if ('clear' in memoizedGuestsQuery && typeof memoizedGuestsQuery.clear === 'function') {
      memoizedGuestsQuery.clear()
    }
  }

  async addBookingGuest(guestInfo: AddBookingGuestVariables) {
    try {
      await arriere.guests.addBookingGuest(guestInfo).then(({ data, errors }) => {
        if (errors && errors.length) {
          trackError(errors[0]?.message, 'Guest: Add Booking Guest Error')
          throw new Error(errors[0]?.message)
        }

        if (data) {
          SidePanelStore.setCurrentSidePanel(undefined)
          Alert.Success('Added new guest successfully!')
          trackEvent('Guest: Added booking guest successfully', { ...guestInfo })
          BookingStore.updateBookingValuesFromServer()
        }
      })
    } catch (error) {
      Alert.Error(`Oops! We were not able to process your request.`)
    }
  }

  async createBookingGuest(guestInfo: CreateBookingGuestVariables) {
    try {
      await arriere.guests.createBookingGuest(guestInfo).then(({ data, errors }) => {
        if (errors && errors.length) {
          trackError(errors[0]?.message, 'Guest: An error occurred when creating new guest.')
          throw new Error(errors[0]?.message)
        }

        if (data) {
          SidePanelStore.setCurrentSidePanel(undefined)
          Alert.Success('Created new guest successfully!')
          trackEvent('Guest: Created new guest successfully', { ...guestInfo })
          BookingStore.updateBookingValuesFromServer()
        }
      })
    } catch (error) {
      Alert.Error(`Oops! We were not able to process your request.`)
    }
  }
}

export const GuestsStore = new _GuestsStore()
